// Game-system pipeline. The frame loop in main.ts feeds a FrameCtx into
// runStages() over this list; ordering and gates are declarative.
//
// Adding a system: append one entry below. Stage controls when it runs;
// `order` breaks ties within a stage (used sparingly, e.g. movement-after-
// skill-active in 'simulation'). `enabled` predicates gate per-frame.
//
// Cross-cutting signals ride the event bus: systems emit facts
// (damage.taken, boss.killed, …), and narrator/camera/UI glue subscribe.
// main.ts drains the bus once per simulated frame.

import { wanderSystem } from './systems/wander'
import { creatureBehaviorSystem } from './systems/creature-behavior'
import { colonySystem } from './systems/colony'
import { sporePufferSystem } from './systems/spore-puffer'
import { skillActiveSystem } from './systems/skill-active'
import { movementSystem } from './systems/movement'
import { predatorAISystem } from './systems/predator-ai'
import { chainBossAISystem } from './systems/chain-boss-ai'
import { allySystem } from './systems/ally'
import { collisionSystem } from './systems/collision'
import { wallsSystem } from './systems/walls'
import { doorsSystem } from './systems/doors'
import { getActiveDungeon } from './dungeon/active'
import type { DoorPlug } from './dungeon/types'
import { absorptionSystem } from './systems/absorption'
import { dnaOrbSystem } from './systems/dna-orb'
import { hazardSystem } from './systems/hazards'
import { damageSystem } from './systems/damage'
import { itemOrbSystem } from './systems/item-orb'
import { npcSystem } from './systems/npc'
import type { NpcSystemDeps } from './systems/npc'
import { motherSystem } from './systems/mother'
import { checkpointSystem } from './systems/checkpoint'
import { particleSystem, updateRipples } from './systems/particles'
import { eyesSystem } from './systems/eyes'
import { spawnSystem } from './systems/spawn'
import { spawnPredatorsSystem } from './systems/spawn-predators'
import { orbitersSystem } from './systems/orbiters'
import { aimSystem } from './systems/aim'
import { attackSystem } from './systems/attack'
import { portalSystem } from './systems/portal'
import { projectilesSystem } from './systems/projectiles'
import { galvanismSystem } from './systems/galvanism'
import { grazeSystem } from './graze'
import { regenSystem } from './systems/regen'
import { evolutionSystem } from './systems/evolution'
import { createNarratorSystems } from './systems/narrator'
import { createLineageSystem } from './systems/lineage'
import { ambientAudioSystem } from './systems/ambient'
import { recomputeMods } from './mods'
import { compilePipeline, type System } from './scheduler'
import type { EventBus } from './events'
import type { ItemInstance } from './components'
import type { GameWorld } from './world'

export interface PipelineDeps {
  npcCallbacks: NpcSystemDeps
  /** Item orb collected (A3): open the compare card + persist. claimKey set
   *  for gate-vault loot (B4) — record the once-per-save flag. */
  onItemPickup: (item: ItemInstance, claimKey?: string) => void
  /** Shortcut door opened (B3): record in world.doors + persist. */
  onDoorOpened: (door: DoorPlug) => void
}

export function buildPipeline(events: EventBus, deps: PipelineDeps): System[] {
  return compilePipeline([
    // input — writes player.aim before anything downstream reads it (attack,
    // dash). Cursor users get raw aim; everyone else gets heading + soft lock.
    { name: 'aim', stage: 'input', run: ctx => aimSystem(ctx.world, ctx.playerRef.current, ctx.input, ctx.dt, ctx.now) },

    // ai — mods folds every stat source first (order -1): predator-ai reads
    // detection range this same stage, and everything downstream reads the rest.
    { name: 'mods',           stage: 'ai', order: -1, run: ctx => recomputeMods(ctx.playerRef.current) },
    { name: 'wander',         stage: 'ai', run: ctx => wanderSystem(ctx.world, ctx.dt, ctx.elapsed) },
    { name: 'creature-behavior', stage: 'ai', order: 0.5, run: ctx => creatureBehaviorSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.elapsed) },
    { name: 'colony',            stage: 'ai', order: 0.6, run: ctx => colonySystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.elapsed) },
    { name: 'spore-puffer',      stage: 'ai', order: 0.7, run: ctx => sporePufferSystem(ctx.world, ctx.playerRef.current, ctx.dt) },
    { name: 'predator-ai',    stage: 'ai', order: 1, run: ctx => predatorAISystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.elapsed, ctx.events) },
    { name: 'chain-boss-ai',  stage: 'ai', order: 2, run: ctx => chainBossAISystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },
    { name: 'ally',           stage: 'ai', order: 3, run: ctx => allySystem(ctx.world, ctx.playerRef.current, ctx.dt) },

    // simulation — skill-active before movement (sets dash impulse); regen + evolution after.
    { name: 'skill-active', stage: 'simulation', run: ctx => skillActiveSystem(ctx.playerRef.current, ctx.input, ctx.dt, ctx.events) },
    { name: 'attack', stage: 'simulation', order: 0.5, run: ctx => attackSystem(ctx.world, ctx.playerRef.current, ctx.input, ctx.dt, ctx.events) },
    { name: 'movement',     stage: 'simulation', order: 1, run: ctx => {
        const speedMult = ctx.playerRef.current.mods?.speedMult ?? 1
        movementSystem(ctx.world, ctx.playerRef.current, ctx.input, ctx.dt, speedMult)
      } },
    // Walls clamp positions right after movement integrates them, so no
    // other system ever observes a body inside the dungeon walls.
    { name: 'walls', stage: 'simulation', order: 1.5,
      enabled: () => getActiveDungeon() !== null,
      run: ctx => wallsSystem(ctx.world) },
    // Doors after walls: the wall push leaves the player resting against
    // the plug, which is exactly the "pressing on it" contact doors read.
    { name: 'doors', stage: 'simulation', order: 1.6,
      enabled: () => (getActiveDungeon()?.doors?.length ?? 0) > 0,
      run: ctx => doorsSystem(ctx.playerRef.current, ctx.events, deps.onDoorOpened) },
    // Portal after walls: gate contact reads clamped positions, and a cast
    // teleport lands before anything downstream observes the old spot.
    { name: 'portal', stage: 'simulation', order: 1.7,
      run: ctx => portalSystem(ctx.world, ctx.playerRef.current, ctx.input, ctx.dt, ctx.events) },
    { name: 'regen', stage: 'simulation', order: 2,
      enabled: ctx => (ctx.playerRef.current.mods?.regenRate ?? 0) > 0,
      run: ctx => regenSystem(ctx.playerRef.current, ctx.dt) },
    { name: 'evolution', stage: 'simulation', order: 3, run: ctx => evolutionSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },

    // collision
    { name: 'collision',  stage: 'collision', run: ctx => collisionSystem(ctx.world, ctx.playerRef.current, ctx.events) },
    { name: 'absorption', stage: 'collision', order: 1, run: ctx => absorptionSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },

    // effects — particles + ripples must run after verbs that emit them so
    // freshly-spawned particles get a frame before being aged. Narrator systems
    // (biome-watch, narrator) sort after these via order 8/9.
    { name: 'dna-orb',      stage: 'effects', run: ctx => dnaOrbSystem(ctx.world, ctx.playerRef.current, ctx.events, ctx.dt) },
    { name: 'hazard',       stage: 'effects', order: 1, run: ctx => hazardSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },
    { name: 'damage',       stage: 'effects', order: 2, run: ctx => damageSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },
    { name: 'orbiters',     stage: 'effects', order: 2.5, run: ctx => orbitersSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },
    { name: 'projectiles',  stage: 'effects', order: 2.6, run: ctx => projectilesSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },
    { name: 'galvanism',    stage: 'effects', order: 2.7, run: ctx => galvanismSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },
    { name: 'item-orb',     stage: 'effects', order: 3.2, run: ctx => itemOrbSystem(ctx.world, ctx.playerRef.current, ctx.dt, deps.onItemPickup) },
    { name: 'npc',          stage: 'effects', order: 4, run: ctx => npcSystem(ctx.world, ctx.playerRef.current, ctx.dt, deps.npcCallbacks) },
    { name: 'mother',       stage: 'effects', order: 4.5, run: ctx => motherSystem(ctx.world, ctx.playerRef.current, ctx.dt) },
    { name: 'checkpoint',   stage: 'effects', order: 4.6, run: ctx => checkpointSystem(ctx.world, ctx.playerRef.current, ctx.dt, ctx.events) },
    { name: 'particles',    stage: 'effects', order: 5, run: ctx => particleSystem(ctx.world, ctx.dt) },
    { name: 'ripples',      stage: 'effects', order: 6, run: ctx => updateRipples(ctx.dt) },
    { name: 'eyes',         stage: 'effects', order: 7, run: ctx => eyesSystem(ctx.world, ctx.dt) },
    createLineageSystem(events),
    ...createNarratorSystems(events),
    { name: 'ambient-audio', stage: 'effects', order: 10, run: ctx => ambientAudioSystem(ctx.world, ctx.playerRef.current) },

    // spawning — graze regen ticks before microbe spawn reads the field.
    { name: 'graze-regen',     stage: 'spawning', run: ctx => grazeSystem(ctx.dt) },
    { name: 'spawn-microbes',  stage: 'spawning', order: 0.5, run: ctx => spawnSystem(ctx.world, ctx.playerRef.current) },
    { name: 'spawn-predators', stage: 'spawning', order: 1, run: ctx => spawnPredatorsSystem(ctx.world, ctx.playerRef.current, ctx.events, ctx.elapsed) },

    // camera — shake decay then velocity-lead follow, after simulation so the
    // camera sees final positions.
    { name: 'camera-follow', stage: 'camera', run: ctx => {
        const cam = ctx.camera
        const p = ctx.playerRef.current
        cam.tickShake(ctx.dt)
        const vx = p.velocity?.x ?? 0
        const vy = p.velocity?.y ?? 0
        const speed = Math.sqrt(vx * vx + vy * vy)
        const capSpeed = Math.min(speed, 0.3)
        const leadX = speed > 0.001 ? (vx / speed) * capSpeed * 0.25 : 0
        const leadY = speed > 0.001 ? (vy / speed) * capSpeed * 0.25 : 0
        const lerpFactor = 1 - Math.exp(-5 * ctx.dt)
        cam.x += (p.position.x + leadX - cam.x) * lerpFactor
        cam.y += (p.position.y + leadY - cam.y) * lerpFactor
      } },
  ] satisfies System[])
}

// Re-export for main's convenience.
export type { GameWorld }
